import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFriends } from '../hooks/useFriends';
import { useAuth } from '../hooks/useAuth';

// MUI components
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifications, markNotificationRead, respondToFriendRequest } = useFriends();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  
  useEffect(() => {
    // Count unread notifications
    if (notifications) {
      const count = notifications.filter(n => !n.is_read).length;
      setUnreadCount(count);
    }
  }, [notifications]);
  
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  
  const handleNotificationClick = async (notification) => {
    // Mark as read
    await markNotificationRead(notification.id);
    
    // Handle different notification types
    if (notification.type === 'friend_request') {
      // Navigate to friends page
      navigate('/friends');
    } else if (notification.type === 'party_invitation') {
      // Navigate to the party
      navigate(`/party/${notification.content.party_id}`);
    }
    
    handleCloseMenu();
  };
  
  const handleFriendRequestResponse = async (notification, accept) => {
    try {
      // Get the friend request ID from the notification
      const requestId = notification.content.friend_request_id;
      
      // Respond to the friend request
      await respondToFriendRequest(requestId, accept);
      
      // Mark the notification as read
      await markNotificationRead(notification.id);
      
      handleCloseMenu();
    } catch (error) {
      console.error('Error responding to friend request:', error);
    }
  };
  
  // Render notification content based on type
  const renderNotificationContent = (notification) => {
    switch (notification.type) {
      case 'friend_request':
        return (
          <>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Friend Request" 
              secondary={`You have a new friend request`}
            />
            <Box>
              <IconButton 
                size="small" 
                color="success"
                onClick={() => handleFriendRequestResponse(notification, true)}
              >
                <CheckIcon />
              </IconButton>
              <IconButton 
                size="small" 
                color="error"
                onClick={() => handleFriendRequestResponse(notification, false)}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </>
        );
      case 'friend_request_accepted':
        return (
          <>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Friend Request Accepted" 
              secondary={`Your friend request was accepted`}
            />
          </>
        );
      case 'party_invitation':
        return (
          <>
            <ListItemIcon>
              <EventIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Party Invitation" 
              secondary={`You've been invited to a party`}
            />
          </>
        );
      default:
        return (
          <ListItemText 
            primary="Notification" 
            secondary={`You have a new notification`}
          />
        );
    }
  };
  
  if (!user) return null;
  
  return (
    <>
      <IconButton 
        color="inherit" 
        onClick={handleOpenMenu}
        aria-label={`${unreadCount} unread notifications`}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            width: 320,
            maxHeight: 400,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
          Notifications
        </Typography>
        
        <Divider sx={{ my: 1 }} />
        
        {notifications && notifications.length > 0 ? (
          notifications.slice(0, 5).map((notification) => (
            <MenuItem 
              key={notification.id} 
              onClick={() => handleNotificationClick(notification)}
              sx={{ 
                py: 1, 
                px: 2,
                backgroundColor: notification.is_read ? 'inherit' : 'action.hover'
              }}
            >
              {renderNotificationContent(notification)}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </MenuItem>
        )}
        
        {notifications && notifications.length > 5 && (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                navigate('/notifications');
                handleCloseMenu();
              }}
            >
              View all notifications
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell; 